import { useEffect, useRef, useState, useCallback } from "react";
import { authHeader } from "@/lib/seoApi";

/**
 * useAutoPoll — small hook that fetches a JSON endpoint on mount
 * and every `intervalMs` afterwards. Returns { data, loading, error,
 * refresh, lastUpdated }.
 *
 * Cancels in-flight requests on unmount. Skips polling when the tab
 * is hidden (document.visibilityState !== 'visible') to save API
 * budget.
 */
export function useAutoPoll(url, { intervalMs = 12000, enabled = true } = {}) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [lastUpdated, setLast]  = useState(null);
  const abortRef                = useRef(null);
  const timerRef                = useRef(null);
  const mountedRef              = useRef(true);

  const fetchOnce = useCallback(async () => {
    if (!enabled || !url) return;
    if (document.visibilityState === "hidden") return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const r = await fetch(url, { headers: authHeader(), signal: ac.signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      if (!mountedRef.current) return;
      setData(body);
      setError(null);
      setLast(new Date());
    } catch (e) {
      if (e.name === "AbortError") return;
      if (!mountedRef.current) return;
      setError(e.message || "fetch failed");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [url, enabled]);

  useEffect(() => {
    mountedRef.current = true;
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, intervalMs);
    const onVis = () => document.visibilityState === "visible" && fetchOnce();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchOnce, intervalMs]);

  return { data, loading, error, refresh: fetchOnce, lastUpdated };
}
